import { useMutation } from '@tanstack/react-query';

import { useInvalidateAppQuery } from '@/hooks/useInvalidateAppQuery';
import { api } from '@/lib/api-client';
import type { MutationConfig } from '@/lib/react-query';
import { toaster } from '@/lib/toaster';

import type { PublishSandboxResponse } from '../models/publishSandbox';

interface PublishSandboxParams {
  sandboxId: string;
  gameCode: string;
}

export type { PublishSandboxResponse };

export const publishSandbox = async ({
  sandboxId,
}: PublishSandboxParams): Promise<PublishSandboxResponse> => {
  const response = await api.post<PublishSandboxResponse>(
    `/v1/sandbox/${sandboxId}/publish`,
  );
  return response.data;
};

const totalFailed = (response: PublishSandboxResponse) =>
  response.items.failed +
  response.snapshots.failed +
  response.gifts.failed +
  response.season_passes.failed;

interface UsePublishSandboxOptions {
  mutationConfig?: MutationConfig<typeof publishSandbox>;
}

export const usePublishSandbox = ({
  mutationConfig,
}: UsePublishSandboxOptions = {}) => {
  const invalidateAppQuery = useInvalidateAppQuery();
  const { onSuccess, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: publishSandbox,
    onSuccess: async (...args) => {
      await invalidateAppQuery({ queryKey: [], refetchType: 'all' });
      // A 200 can still report failures per resource type; the summary dialog
      // carries the details, so only claim success when nothing failed.
      if (totalFailed(args[0]) === 0) {
        toaster.success('Sandbox published successfully');
      }
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};
